import React, { useEffect, useState } from "react";
import { Box, Flex, Image, Stack, Text, HStack, Tooltip } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

import Brand from "./Brand";
import LogoutModal from "../logoutModal";
import { SSORole } from "common/contexts/SSOContext";
import SettingIconDark from "common/assets/img/settingIconDark.png";
import LogoutIcon from "common/assets/img/logoutIconRed.png";
import AngleDown from "common/assets/img/angleDownDark.png";
import crmIconDark from "common/assets/img/crmIconDark.png";
import passwordIconFDark from "common/assets/img/passwordIconDark.png";
import ActivitiesIcon from "common/assets/img/activitiesDarkIcon.png"
import WorkflowIcon from "common/assets/img/workflowIconDark.png"
import ReportingIcon from "common/assets/img/reportingIconDark.png"
import TaskIcon from "common/assets/img/taskIconDark.png"
import ProductIcon from "common/assets/img/productsIconDark.png"
import SupportIcon from "common/assets/svg/SupportIcon";
import OverviewIcon from "common/assets/svg/OverViewIcon";

function SidebarContent(props) {
  const navigate = useNavigate();
  const location = useLocation();

  const [showSettingHover, setShowSettingHover] = useState(false);
  const [showLogoutHover, setShowLogoutHover] = useState(false);
  const [hoverActive, setHoverActive] = useState(null);
  const [openSubMenus, setOpenSubMenus] = useState({ 0: true }); // Start with first menu open
  const [nestedMenus, setNestedMenus] = useState({}); // Track nested menu states
  const [isLogoutScheduled, setIsLogoutScheduled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLabelVisibleInfo, setIsLabelVisibleInfo] = useState({
    isLabelVisible: false,
    isLabelName: "",
  });

  const activeColor = "#01509e";
  const activeIcon = "white";
  const textColor = "secondaryGray.500";
  const sidebarBgColor = "#383f57";

  const {
    isFirstLevelMenu,
    organizedRoutes = [],
    isNavbarMenu,
    setOpenSidebar,
    openSidebar,
    isUserLayout,
    from,
    largeLogo,
    dynamic,
  } = props;

  const { intervalId } = SSORole();

  const logOut = (message) => {
    const rememberedUser = localStorage.getItem("rememberedUser");
    localStorage.clear();
    sessionStorage.clear();
    rememberedUser && localStorage.setItem("rememberedUser", rememberedUser);
    clearInterval(intervalId);
    navigate("/auth");
    if (message) {
      toast.error(message);
    } else {
      toast.success("Log out Successfully");
    }
    setIsLogoutScheduled(true);
  };

const toggleSubMenu = (index) => {
  setOpenSubMenus((prev) => {
    const newMenus = {};
    Object.keys(prev).forEach((key) => {
      newMenus[key] = false;
    });
    newMenus[index] = !prev[index];
    return newMenus;
  });
};

  const toggleNestedMenu = (parentIndex, subIndex) => {
    const key = `${parentIndex}-${subIndex}`;
    setNestedMenus((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const activeRoute = (routeName) => {
    return location.pathname?.toLowerCase().includes(routeName.toLowerCase());
  };

  const isSubRouteActive = (subRoutes) => {
    if (!Array.isArray(subRoutes)) return false;
    return subRoutes.some(route => {
      if (route.path && activeRoute(route.path)) return true;
      if (route.subRoutes) return isSubRouteActive(route.subRoutes);
      return false;
    });
  };

  const handleNavigation = (path) => {
    if (path) {
      try {
        localStorage.removeItem("searchTerm");
        localStorage.removeItem("selectedTabIndex");
        navigate(path);
      } catch (error) {
        console.error("Navigation error:", error);
        window.location.href = path;
      }
    }
  };

  useEffect(() => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000; // Convert milliseconds to seconds
        if (decodedToken.exp < currentTime) {
          if (!isLogoutScheduled) {
            logOut("Token has expired");
          }
        } else {
          // Schedule automatic logout when the token expires
          const timeToExpire = (decodedToken.exp - currentTime) * 1000; // Convert seconds to milliseconds
          setTimeout(() => {
            if (!isLogoutScheduled) {
              logOut("Token has expired");
            }
          }, timeToExpire);
        }
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, [isLogoutScheduled]);

  // Auto-expand menu if subroute is active
  useEffect(() => {
    const newOpenMenus = { ...openSubMenus };
    const newNestedMenus = { ...nestedMenus };

    organizedRoutes.forEach((route, index) => {
      if (route.subRoutes && isSubRouteActive(route.subRoutes)) {
        newOpenMenus[index] = true;

        route.subRoutes.forEach((subRoute, subIndex) => {
          if (subRoute.subRoutes && isSubRouteActive(subRoute.subRoutes)) {
            newNestedMenus[`${index}-${subIndex}`] = true;
          }
        });
      }
    });

    setOpenSubMenus(newOpenMenus);
    setNestedMenus(newNestedMenus);
  }, [location.pathname]);

  // Render individual menu item with proper icon handling
  const renderMenuItem = (route, level = 0, parentIndex = 0, subIndex = 0) => {
    const hasSubRoutes = route.subRoutes && Array.isArray(route.subRoutes) && route.subRoutes.length > 0;
    const isRouteActive = route.path ? activeRoute(route.path) : isSubRouteActive(route.subRoutes);
    const isHovered = hoverActive === `${route.name}-${level}-${parentIndex}-${subIndex}`;
    const hoverKey = `${route.name}-${level}-${parentIndex}-${subIndex}`;


    const getDefaultIcon = (routeName) => {
      const iconStyle = {
        width: "16px",
        height: "16px",
        opacity: "1"
      };

      const iconMap = {
        'CRM': <Image
          src={crmIconDark}
          style={{ opacity: "1" }}
          h="16px"
          w="16px"
        />,

        'Activities': <Image
          src={ActivitiesIcon}
          style={{ opacity: "1" }}
          h="16px"
          w="16px"
        />,
        'Reports': <Image
          src={ReportingIcon}
          style={{ opacity: "1" }}
          h="16px"
          w="16px"
        />,
        'Workflows': <Image
          src={WorkflowIcon}
          style={{ opacity: "1" }}
          h="16px"
          w="16px"
        />,

        'Password Manager': <Image
          src={passwordIconFDark}
          style={{ opacity: "1" }}
          h="16px"
          w="16px"
        />,
        'Settings': <Image
          objectFit="cover"
          src={SettingIconDark}
          style={{ opacity: "1" }}
          h="16px"
          w="16px"
        />,
        'Support': (
          <Box style={iconStyle}>
            <SupportIcon />
          </Box>
        ),
        'Get Started': (
          <Box style={iconStyle}>
            <OverviewIcon />
          </Box>
        )
      };

      return iconMap[routeName] || <Box className="subroutesDotIcon" />;
    };

    const renderIcon = () => {
      if (route.iconUrl) {
        return <img src={route.iconUrl} width="20px" height="20px" alt={route.name} />;
      }

      if (route.icon) {
        try {
          return route.icon;
        } catch (error) {
          console.warn(`Error rendering icon for ${route.name}:`, error);
          return getDefaultIcon(route.name);
        }
      }

      // Default icon
      return getDefaultIcon(route.name);
    };

    return (
      <Box
        key={`menu-item-${route.name}-${level}-${parentIndex}-${subIndex}`}
        className={`menu-item level-${level}`}
        ml={level > 0 ? 6 : 0}
        mt={level > 0 ? 1 : 0}
      >
        {/* Menu Item */}
        <Box
          onClick={() => {
            if (route.path && (!hasSubRoutes || level > 0)) {
              // Navigate if it has a path and either no subroutes or it's not top level
              handleNavigation(route.path);
            } else if (hasSubRoutes && level === 0) {
              // Toggle submenu for top level items with subroutes
              toggleSubMenu(parentIndex);
            } else if (hasSubRoutes && level === 1) {
              // Toggle nested submenu for second level items with subroutes
              toggleNestedMenu(parentIndex, subIndex);
            }
          }}
          cursor="pointer"
          className={`${isRouteActive || isHovered ? "sideTab activeSideTab" : "sideTab"}`}
          backgroundColor={isRouteActive || isHovered ? sidebarBgColor : ""}
          ps="25px"
          pb="6px"
          pt="10px"
          onMouseEnter={() => {
            setIsLabelVisibleInfo({
              isLabelName: route.name,
              isLabelVisible: true,
            });
            setHoverActive(hoverKey);
          }}
          onMouseLeave={() => {
            setIsLabelVisibleInfo({
              isLabelName: "",
              isLabelVisible: false,
            });
            setHoverActive(null);
          }}
        >
          <HStack
            className={`${isRouteActive ? "activeBorder customStack" : "customStack"}`}
            spacing={isRouteActive ? "22px" : "26px"}
            py="5px"
          >
            {openSidebar === true ? (
              <Flex w="100%" alignItems="center" justifyContent="center">
                <Box color={isRouteActive ? activeIcon : textColor}>
                  {renderIcon()}
                </Box>
                <Text
                  me="auto"
                  pb="3px"
                  textOverflow="ellipsis"
                  overflowX="hidden"
                  whiteSpace="nowrap"
                  className="linktext"
                  color={isRouteActive ? activeColor : textColor}
                  fontWeight={isRouteActive ? "bold" : "normal"}
                >
                  <Tooltip label={route.name}>{route.name}</Tooltip>
                </Text>
              </Flex>
            ) : (
              <Flex w="100%" alignItems="center">
                <Box
                  className={`left-link ${route.name === "Customize Dashboard" ? "cus-das" : ""}`}
                  color={isRouteActive ? activeIcon : textColor}
                  me="18px"
                >
                  <Box display="flex" alignItems="center" gap="8px">
                    {renderIcon()}
                    <Text className="tab-linkname">{route.name}</Text>
                  </Box>
                  {hasSubRoutes && level === 0 && (
                    <Box className="linkArrow">
                      <Image
                        src={AngleDown}
                        height="14px"
                        width="14px"
                        alt=""
                        transform={openSubMenus[parentIndex] ? "rotate(180deg)" : "rotate(0deg)"}
                        transition="transform 0.2s"
                      />
                    </Box>
                  )}
                  <div
                    className="hoverName"
                    style={{
                      display: isLabelVisibleInfo.isLabelName === route.name && isLabelVisibleInfo.isLabelVisible ? "flex" : "none",
                    }}
                    label={route.name}
                  >
                    {route.name}
                  </div>
                </Box>
              </Flex>
            )}
          </HStack>
        </Box>
      </Box>
    );
  };

  return (
    <Flex direction="column" width="100%" height="100%" borderRadius="30px">
      <Brand
        from={from}
        largeLogo={largeLogo}
        openSidebar={openSidebar}
        setOpenSidebar={setOpenSidebar}
      />

      <Stack
        direction="column"
        mb="auto"
        pt={2}
        className="leftSidebar-container sidebar-overflow top-menu-container"
        flex="1"
      >
        <Box className="top-menu" flex="1">
          {organizedRoutes.map((route, index) => {
            if (route.isSeparator) {
              return (
                <Box key={`separator-${index}`} className="menu-separator" my={4}>
                  <hr style={{
                    border: 'none',
                    borderTop: '1px solid #4a5568',
                    margin: '0',
                    opacity: 0.3,
                    width: '100%'
                  }} />
                  {route.separatorText && (
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      textAlign="center"
                      mt={2}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {route.separatorText}
                    </Text>
                  )}
                </Box>
              );
            }

            const hasSubRoutes = route.subRoutes && route.subRoutes.length > 0;
            const isMenuOpen = openSubMenus[index];

            return (
              <Box key={`main-menu-${index}`} className="menuItemBox">
                {renderMenuItem(route, 0, index, 0)}

                {hasSubRoutes && isMenuOpen && (
                  <Box className="subRoutes">
                    {route.subRoutes.map((subRoute, subIndex) => {
                      const hasNestedSubRoutes = subRoute.subRoutes && subRoute.subRoutes.length > 0;
                      const nestedKey = `${index}-${subIndex}`;
                      const isNestedOpen = nestedMenus[nestedKey];

                      return (
                        <Box key={`sub-${index}-${subIndex}`} className="subRouteItem">
                          {renderMenuItem(subRoute, 1, index, subIndex)}

                          {hasNestedSubRoutes && isNestedOpen && (
                            <Box className="nestedSubRoutes">
                              {subRoute.subRoutes.map((nestedRoute, nestedIndex) => (
                                <Box key={`nested-${index}-${subIndex}-${nestedIndex}`}>
                                  {renderMenuItem(nestedRoute, 2, index, `${subIndex}-${nestedIndex}`)}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}

          {location.pathname === "/admin-setting" && (
            <Text className="adminPageTitle" mt="13px" fontWeight="500" fontSize="18px">
              Admin Settings
            </Text>
          )}
        </Box>

        {/* Bottom Section with Divider */}
        <Box className="bottom-section" mt="auto" pt={4}>
          {/* Divider */}
          <Box className="sidebar-divider" mb={3}>
            <hr style={{
              border: 'none',
              borderTop: '1px solid #4a5568',
              margin: '0',
              opacity: 0.3,
              width: '100%'
            }} />
          </Box>

          {/* Settings */}
          <Box className="bottom-menu-item">
            <Box
              onClick={() => navigate(`/userView/${JSON.parse(localStorage.getItem("user"))?._id}`)}
              cursor="pointer"
              className={`${activeRoute("/userView") || hoverActive === "settings"
                ? "sideTab activeSideTab"
                : "sideTab"
                }`}
              backgroundColor={
                activeRoute("/userView") || hoverActive === "settings"
                  ? sidebarBgColor
                  : ""
              }
              ps="25px"
              pb="6px"
              pt="10px"
              onMouseEnter={() => {
                setIsLabelVisibleInfo({
                  isLabelName: "Settings",
                  isLabelVisible: true,
                });
                setHoverActive("settings");
              }}
              onMouseLeave={() => {
                setIsLabelVisibleInfo({
                  isLabelName: "",
                  isLabelVisible: false,
                });
                setHoverActive(null);
              }}
            >
              <HStack
                className={`${activeRoute("/userView") ? "activeBorder customStack" : "customStack"}`}
                spacing={activeRoute("/userView") ? "22px" : "26px"}
                py="5px"
              >
                {openSidebar === true ? (
                  <Flex w="100%" alignItems="center" justifyContent="center">
                    <Box color={activeRoute("/userView") ? activeIcon : textColor}>
                      <Image
                        objectFit="cover"
                        src={SettingIconDark}
                        style={{ opacity: "1" }}
                        h="16px"
                        w="16px"
                      />
                    </Box>
                    <Text
                      me="auto"
                      pb="3px"
                      textOverflow="ellipsis"
                      overflowX="hidden"
                      whiteSpace="nowrap"
                      className="linktext"
                      color={activeRoute("/userView") ? activeColor : textColor}
                      fontWeight={activeRoute("/userView") ? "bold" : "normal"}
                    >
                      <Tooltip label="Settings">Settings</Tooltip>
                    </Text>
                  </Flex>
                ) : (
                  <Flex w="100%" alignItems="center">
                    <Box
                      className="left-link"
                      color={activeRoute("/userView") ? activeIcon : textColor}
                      me="18px"
                    >
                      <Box display="flex" alignItems="center" gap="8px">
                        <Image
                          objectFit="cover"
                          src={SettingIconDark}
                          style={{ opacity: "1" }}
                          h="16px"
                          w="16px"
                        />
                        <Text className="tab-linkname">Settings</Text>
                      </Box>
                      <div
                        className="hoverName"
                        style={{
                          display: isLabelVisibleInfo.isLabelName === "Settings" && isLabelVisibleInfo.isLabelVisible ? "flex" : "none",
                        }}
                        label="Settings"
                      >
                        Settings
                      </div>
                    </Box>
                  </Flex>
                )}
              </HStack>
            </Box>
          </Box>

          {/* Support */}
          <Box className="bottom-menu-item">
            <Box
              onClick={() => navigate("/support")}
              cursor="pointer"
              className={`${activeRoute("/support") || hoverActive === "support"
                ? "sideTab activeSideTab"
                : "sideTab"
                }`}
              backgroundColor={
                activeRoute("/support") || hoverActive === "support"
                  ? sidebarBgColor
                  : ""
              }
              ps="25px"
              pb="6px"
              pt="10px"
              onMouseEnter={() => {
                setIsLabelVisibleInfo({
                  isLabelName: "Support",
                  isLabelVisible: true,
                });
                setHoverActive("support");
              }}
              onMouseLeave={() => {
                setIsLabelVisibleInfo({
                  isLabelName: "",
                  isLabelVisible: false,
                });
                setHoverActive(null);
              }}
            >
              <HStack
                className={`${activeRoute("/support") ? "activeBorder customStack" : "customStack"}`}
                spacing={activeRoute("/support") ? "22px" : "26px"}
                py="5px"
              >
                {openSidebar === true ? (
                  <Flex w="100%" alignItems="center" justifyContent="center">
                    <Box color={activeRoute("/support") ? activeIcon : textColor}>
                      <Box style={{ width: "16px", height: "16px", opacity: "1" }}>
                        <SupportIcon />
                      </Box>
                    </Box>
                    <Text
                      me="auto"
                      pb="3px"
                      textOverflow="ellipsis"
                      overflowX="hidden"
                      whiteSpace="nowrap"
                      className="linktext"
                      color={activeRoute("/support") ? activeColor : textColor}
                      fontWeight={activeRoute("/support") ? "bold" : "normal"}
                    >
                      <Tooltip label="Support">Support</Tooltip>
                    </Text>
                  </Flex>
                ) : (
                  <Flex w="100%" alignItems="center">
                    <Box
                      className="left-link"
                      color={activeRoute("/support") ? activeIcon : textColor}
                      me="18px"
                    >
                      <Box display="flex" alignItems="center" gap="8px">
                        <Box style={{ width: "16px", height: "16px", opacity: "1" }}>
                          <SupportIcon />
                        </Box>
                        <Text className="tab-linkname">Support</Text>
                      </Box>
                      <div
                        className="hoverName"
                        style={{
                          display: isLabelVisibleInfo.isLabelName === "Support" && isLabelVisibleInfo.isLabelVisible ? "flex" : "none",
                        }}
                        label="Support"
                      >
                        Support
                      </div>
                    </Box>
                  </Flex>
                )}
              </HStack>
            </Box>
          </Box>
        </Box>

        {/* LOGOUT SECTION - Keep separate at the very bottom */}
        <div className="sidebarBottom">
          <div className="sidebarBottom-container">
            <div
              className={`${hoverActive === "logout"
                ? "sidebarBottom-content activeBox activeSideTab sidebarBottom-border"
                : "sidebarBottom-content sidebarBottom-border"
                }`}
              onClick={() => setIsOpen(true)}
              onMouseLeave={() => {
                setShowLogoutHover(false);
                setHoverActive(null);
              }}
              onMouseEnter={() => {
                setShowLogoutHover(true);
                setHoverActive("logout");
              }}
            >
              <div className="sidebarBottom-linkContainer">
                <Image
                  objectFit="cover"
                  src={LogoutIcon}
                  style={{ opacity: "1" }}
                  h="16px"
                  w="16px"
                />
                <Text className="tab-linkname logoutText">{"Log Out"}</Text>
              </div>

              <div
                className="hoverName"
                style={{
                  display: showLogoutHover ? "flex" : "none",
                }}
                label={"Log Out"}
              >
                {"Log Out"}
              </div>
            </div>
          </div>
        </div>
      </Stack>

      <LogoutModal
        handleLogout={logOut}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
      />
    </Flex>
  );

}

export default SidebarContent;
